import './CatalogBackToTop.css';
import {useLayoutEffect,useRef,useState} from 'react';
import {createPortal} from 'react-dom';
import {Button} from '@platform/ui/Button';
import {useCopy} from '@platform/ui/config';

// One room control follows the active scroll surface, regardless of card count
// or lazy-loading batches. Native dialogs need the button inside their top layer.
export function CatalogBackToTop(){
 const t=useCopy(),anchor=useRef<HTMLSpanElement>(null);
 const [view,setView]=useState<{host:HTMLElement|null;scroller:HTMLElement|null;visible:boolean;right:number;bottom:number}>({host:null,scroller:null,visible:false,right:12,bottom:12});
 useLayoutEffect(()=>{
  const room=anchor.current?.closest<HTMLElement>('.game-room');if(!room)return;
  let frame=0,observed:Element[]=[],lastScroll:HTMLElement|null=null;
  let dialogs:HTMLDialogElement[]=[];
  const queue=()=>{if(!frame)frame=requestAnimationFrame(update);};
  const onScroll=(event:Event)=>{if(event.target instanceof HTMLElement)lastScroll=event.target;queue();};
  const resize=new ResizeObserver(queue);
  const update=()=>{
   frame=0;
   const open=Array.from(room.querySelectorAll<HTMLDialogElement>('dialog[open]'));
   // Preserve actual opening order when sibling dialogs overlap.
   dialogs=dialogs.filter(dialog=>open.includes(dialog));
   for(const dialog of open)if(!dialogs.includes(dialog))dialogs.push(dialog);
   const dialog=dialogs.at(-1),host=dialog??room;
   const column=host.querySelector<HTMLElement>('.room-play-column')??host;
   const footer=host.querySelector<HTMLElement>('.bottom-cta');
   const bounds=host.getBoundingClientRect(),columnBounds=column.getBoundingClientRect();
   const rightEdge=columnBounds.width>0?columnBounds.right:bounds.right;
   const footerBounds=footer?.getBoundingClientRect();
   const surfaces=Array.from(host.querySelectorAll<HTMLElement>('[data-dialog-scroll]')).filter(e=>e.clientHeight>0);
   const scroller=surfaces.find(e=>e===lastScroll)??surfaces.find(e=>e.scrollTop>0)??surfaces[0]??dialog??null;
   const bottomDock=footer&&getComputedStyle(footer.closest('.room-footer-dock')??footer).position==='fixed';
   const edge=Math.min(innerHeight,dialog?bounds.bottom:innerHeight,bottomDock&&footerBounds&&footerBounds.bottom>0?footerBounds.top:innerHeight);
   const offset=scroller?.scrollTop??window.scrollY;
   const next={host,scroller,visible:offset>Math.min(240,(scroller?.clientHeight??innerHeight)/2),
    right:Math.max(12,innerWidth-Math.min(innerWidth,rightEdge)+12),bottom:Math.max(12,innerHeight-edge+12)};
   setView(previous=>previous.host===next.host&&previous.scroller===next.scroller&&previous.visible===next.visible&&previous.right===next.right&&previous.bottom===next.bottom?previous:next);
   const targets=[host,column,host.querySelector('.dialog-body'),scroller,footer].filter((element):element is HTMLElement=>!!element);
   if(targets.length!==observed.length||targets.some((element,index)=>observed[index]!==element)){
    resize.disconnect();targets.forEach(element=>resize.observe(element));observed=targets;
   }
  };
  const mutation=new MutationObserver(queue);
  mutation.observe(room,{subtree:true,childList:true,attributes:true,attributeFilter:['open']});
  update();window.addEventListener('scroll',onScroll,{capture:true,passive:true});window.addEventListener('resize',queue);
  return()=>{cancelAnimationFrame(frame);resize.disconnect();mutation.disconnect();window.removeEventListener('scroll',onScroll,true);window.removeEventListener('resize',queue);};
 },[]);
 const goUp=()=>{
  const host=view.host;if(!host)return;
  host.querySelector<HTMLButtonElement>('.card-view-select button,.room-topbar button,.dialog-heading button')?.focus({preventScroll:true});
  const options:ScrollToOptions={top:0,behavior:matchMedia('(prefers-reduced-motion: reduce)').matches?'instant':'smooth'};
  if(view.scroller)view.scroller.scrollTo(options);else window.scrollTo(options);
 };
 return <><span ref={anchor} hidden aria-hidden="true"/>{view.host&&view.visible&&createPortal(
  <Button className="catalog-back-top" style={{right:view.right,bottom:view.bottom}} aria-label={t('cards.backToTop')} title={t('cards.backToTop')} onClick={goUp}>
   <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 5h14M12 20V9m-5 5 5-5 5 5"/></svg>
  </Button>,view.host)}</>;
}
